import type { Messages } from '@/i18n/messages/en/index.ts'

/**
 * `login` in Brazilian Portuguese. Declared `: Messages['login']`, never inferred (LOC-011).
 *
 * This is the screen a Gi deployment is judged by — every one of its readers is a Portuguese
 * speaker — and the one screen a person reads before they have an account whose language
 * could have been looked up. It is also outside `AppShell` (F-23), which is why the switcher
 * appears a second time in its footer: somebody has to be able to change the language before
 * there is anywhere to store the choice.
 *
 * **Entrar**, not a literal rendering of "sign in". Brazilian products say *Entrar* on the
 * button and *Sair* on the way out, and that pair is what the rest of this catalogue uses.
 * *Iniciar sessão* is correct Portuguese and reads as a translation; *Assinar* would mean
 * signing a document.
 *
 * Where English names an administrator in the abstract this says **quem administra**, for
 * the same reason as `shell`: choosing *administrador* or *administradora* for somebody
 * nobody has met is a coin flip the English never had to make (D-21).
 */
export const login: Messages['login'] = {
  title: 'Entrar',

  checkingMethods: 'Verificando as formas de entrar disponíveis…',
  // "Continuar com" takes the provider name directly, the way every Brazilian sign-in screen
  // writes it. The name itself is a proper noun and stays as the issuer spells it (D-17).
  continueWith: (provider) => `Continuar com ${provider}`,

  or: 'ou',

  // "E-mail" with the hyphen: that is the spelling in Brazilian Portuguese, and it is what a
  // person scanning a form expects to see above the field.
  emailLabel: 'E-mail',
  passwordLabel: 'Senha',
  submit: 'Entrar',
  signingIn: 'Entrando…',
  signInFailed: 'Não deu certo. Confira a senha e tente de novo.',
  devNotice: 'Entrada de desenvolvimento. Desativada automaticamente em produção.',

  unreachable:
    'Não é possível entrar no momento — não foi possível alcançar o serviço. Isso costuma ser temporário; tente de novo em instantes. Se continuar acontecendo, quem administra deve verificar se a plataforma está no ar.',

  // The identifier sits mid-sentence in Portuguese too, so the split falls in the same place
  // and the view composes the same two halves around the same `<code>`.
  noMethod: {
    before: 'Nenhuma forma de entrar está configurada. Quem administra precisa definir',
    after: 'antes que alguém consiga entrar.'
  }
}
